import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import { cn } from '@/lib/utils';
import { TRIAGEM_BTN_PRIMARIO, TRIAGEM_CELULA, TRIAGEM_CELULA_FORTE, TRIAGEM_CELULA_NUM, TRIAGEM_TD, TRIAGEM_TH, TRIAGEM_TR_BASE, TRIAGEM_TR_LIVRE } from '../triagem/triagemTabela';

/* NOTA BAIXA (Douglas 28/09/2026): a nota de 1 a 3 é da analista 2. Ela resolve
   o que incomodou e pede a nota de novo; se o cliente der nota maior, conta
   como nota melhorada. A resposta é lida sozinha pelo CRM. */

type NotaBaixa = {
  pedido_id: string; numero_pedido: string; razao_social: string; nota: number; nota_em: string;
  pedida_em: string | null; respondida_em: string | null; nota_melhorada_em: string | null; conversa_id: string | null;
};

const BTN_TEXTO = '!w-auto whitespace-nowrap px-2 text-[10.5px] font-semibold disabled:opacity-40';

const data = (v: string | null) => v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

function situacao(n: NotaBaixa) {
  if (n.nota_melhorada_em) return { texto: `Melhorou para ${n.nota}`, cor: 'text-success' };
  if (n.pedida_em && !n.respondida_em) return { texto: `Pedida em ${data(n.pedida_em)} · esperando`, cor: 'text-primary' };
  if (n.respondida_em) return { texto: 'Respondeu sem melhorar', cor: 'text-muted-foreground' };
  return { texto: 'Resolver e pedir nova nota', cor: 'text-primary' };
}

export function NotasBaixasBoasVindas({ segmento }: { segmento: 'PJ' | 'PF' }) {
  const [ocupado, setOcupado] = useState('');
  const q = useQuery({
    queryKey: ['boas-vindas-notas-baixas', segmento], staleTime: 30_000,
    queryFn: () => requisitarAtivacao<NotaBaixa[]>(`boas-vindas/notas-baixas?segmento=${segmento}`),
  });
  const pedir = async (n: NotaBaixa) => {
    setOcupado(n.pedido_id);
    try {
      await requisitarAtivacao(`boas-vindas/${n.pedido_id}/pedir-nova-nota`, n.conversa_id ? { conversaId: n.conversa_id } : {});
      toast.success('Nova nota pedida ao cliente');
      await q.refetch();
    } catch (e) { toast.error((e as Error).message); }
    finally { setOcupado(''); }
  };
  if (q.isLoading) return <p className="p-3 text-xs text-muted-foreground">Carregando as notas baixas…</p>;
  if (q.error) return <p role="alert" className="p-3 text-xs text-destructive">{(q.error as Error).message}</p>;
  const lista = q.data ?? [];
  const melhoradas = lista.filter(n => n.nota_melhorada_em).length;
  return (
    <div>
      <p className="flex items-center gap-1.5 border-b border-border/40 px-3 py-1.5 text-[10.5px] text-muted-foreground">
        <Star className="h-3 w-3" aria-hidden="true" />
        Nota de 1 a 3 deste mês e do anterior. Resolva com o cliente e peça a nota de novo: nota maior conta como nota melhorada.
        <strong className="ml-auto font-display tabular-nums text-foreground">{melhoradas} de {lista.length} melhoradas</strong>
      </p>
      {!lista.length ? <p className="p-4 text-center text-xs text-muted-foreground">Nenhuma nota baixa para trabalhar.</p> : (
        <table className="w-full table-fixed text-left">
          <thead><tr>
            <th className={cn(TRIAGEM_TH, 'w-[34%]')}>Cliente</th>
            <th className={cn(TRIAGEM_TH, 'w-[12%]')}>Pedido</th>
            <th className={cn(TRIAGEM_TH, 'w-[8%]')}>Nota</th>
            <th className={cn(TRIAGEM_TH, 'w-[12%]')}>Deu em</th>
            <th className={cn(TRIAGEM_TH, 'w-[20%]')}>Situação</th>
            <th className={cn(TRIAGEM_TH, 'w-[14%]')} />
          </tr></thead>
          <tbody>{lista.map(n => {
            const s = situacao(n);
            const podePedir = !n.nota_melhorada_em && !(n.pedida_em && !n.respondida_em && Date.now() - Date.parse(n.pedida_em) < 24 * 3600_000);
            return (
              <tr key={n.pedido_id} className={cn(TRIAGEM_TR_BASE, TRIAGEM_TR_LIVRE)}>
                <td className={TRIAGEM_TD}><span className={TRIAGEM_CELULA_FORTE}>{n.razao_social || '—'}</span></td>
                <td className={TRIAGEM_TD}><span className={TRIAGEM_CELULA}>{n.numero_pedido}</span></td>
                <td className={TRIAGEM_TD}><span className={TRIAGEM_CELULA_NUM}>{n.nota}</span></td>
                <td className={TRIAGEM_TD}><span className={TRIAGEM_CELULA}>{data(n.nota_em)}</span></td>
                <td className={TRIAGEM_TD}><span className={cn('block truncate text-[11px] font-semibold', s.cor)}>{s.texto}</span></td>
                <td className={TRIAGEM_TD}>
                  {podePedir && <button type="button" className={cn(TRIAGEM_BTN_PRIMARIO, BTN_TEXTO)} disabled={ocupado === n.pedido_id} onClick={() => void pedir(n)}
                    title="Resolvi com o cliente: pedir a nota de 1 a 5 de novo">{n.respondida_em ? 'Pedir de novo' : 'Pedir nova nota'}</button>}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}
